/**
 * JourneyRail — the persistent checklist rail for GUIDED JOURNEYS
 * (walkthrough UX option B, Connor pick 2026-07-21: "B, C, D").
 *
 * Appcues/Userflow model: while a journey runs, a slim docked rail shows the
 * WHOLE flow — every step numbered with its state (done ✓ / current / pass ✓ /
 * flagged ⚑ from the test-plan results), a progress bar, and click-to-jump.
 * The page stays fully usable; the rail survives route changes AND persona
 * switches (it reads the walkthrough store, which persists in localStorage).
 *
 * Scope: journeys only (kind 'journey'). The sandbox build-a-dataset
 * simulations keep the classic overlay-only treatment.
 *
 * Collapse state persists so a user can tuck it away mid-journey and get a
 * compact resume pill instead.
 */
import { useState } from 'react'
import { Route, ChevronRight, ChevronLeft, X, Check, Flag } from 'lucide-react'
import { useWalkthroughStore } from '@/modules/walkthrough/walkthroughStore'
import { cn } from '@/lib/utils'

const COLLAPSE_KEY = 'ppm-journey-rail-collapsed'

function loadCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false }
}

export function JourneyRail() {
  const activeScenario = useWalkthroughStore((s) => s.activeScenario)
  const stepIndex = useWalkthroughStore((s) => s.stepIndex)
  const paused = useWalkthroughStore((s) => s.paused)
  const goToStep = useWalkthroughStore((s) => s.goToStep)
  const exit = useWalkthroughStore((s) => s.exit)
  const stepResults = useWalkthroughStore((s) => s.stepResults)
  const [collapsed, setCollapsed] = useState(loadCollapsed)

  const scenario = activeScenario()
  // Journeys only — simulations keep the overlay-only treatment.
  if (!scenario || scenario.kind !== 'journey' || paused) return null

  const results = stepResults[scenario.id] ?? {}
  const doneCount = Math.min(stepIndex, scenario.steps.length)
  const pct = Math.round((doneCount / scenario.steps.length) * 100)

  const setCollapsedPersist = (v: boolean) => {
    setCollapsed(v)
    try { localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0') } catch { /* ignore */ }
  }

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsedPersist(false)}
        aria-label="Expand journey checklist"
        className="fixed right-0 top-1/3 z-[54] flex items-center gap-1.5 rounded-l-xl border border-r-0 border-brand-300 bg-brand-600 px-2 py-3 text-white shadow-lg hover:bg-brand-700 transition-colors"
      >
        <ChevronLeft size={14} />
        <span className="[writing-mode:vertical-rl] text-[10px] font-bold tracking-wider uppercase">
          Journey · step {Math.min(stepIndex + 1, scenario.steps.length)}/{scenario.steps.length}
        </span>
      </button>
    )
  }

  return (
    <aside
      aria-label="Journey checklist"
      className="fixed right-3 top-20 z-[54] w-[230px] rounded-xl border border-border bg-white shadow-xl overflow-hidden"
    >
      <div className="flex items-center gap-2 bg-gradient-to-r from-brand-600 to-indigo-600 px-3 py-2 text-white">
        <Route size={13} className="shrink-0" />
        <span className="flex-1 truncate text-[11px] font-bold leading-tight">{scenario.name}</span>
        <button onClick={() => setCollapsedPersist(true)} aria-label="Collapse journey checklist" className="rounded p-0.5 hover:bg-white/15">
          <ChevronRight size={13} />
        </button>
        <button onClick={exit} aria-label="Exit journey" className="rounded p-0.5 hover:bg-white/15">
          <X size={13} />
        </button>
      </div>
      <div className="px-3 pt-2">
        <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1 text-[10px] text-text-tertiary">{doneCount} of {scenario.steps.length} steps · ~{scenario.estMinutes} min total</div>
      </div>
      <ol className="max-h-[46vh] overflow-y-auto px-1.5 py-1.5">
        {scenario.steps.map((step, i) => {
          const isCur = i === stepIndex
          const isDone = i < stepIndex
          const result = results[step.id]
          return (
            <li key={step.id}>
              <button
                onClick={() => goToStep(i)}
                className={cn(
                  'flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] leading-snug transition-colors',
                  isCur ? 'bg-brand-50 font-semibold text-brand-800' : 'text-text-secondary hover:bg-surface-muted',
                  isDone && !isCur && 'text-text-tertiary',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[9px] font-bold',
                    result === 'pass' ? 'border-emerald-500 bg-emerald-500 text-white'
                    : result === 'flag' ? 'border-amber-500 bg-amber-500 text-white'
                    : isDone ? 'border-emerald-500 text-emerald-600'
                    : isCur ? 'border-brand-500 text-brand-600'
                    : 'border-border text-text-tertiary',
                  )}
                >
                  {result === 'pass' ? <Check size={9} /> : result === 'flag' ? <Flag size={8} /> : isDone ? <Check size={9} /> : i + 1}
                </span>
                <span className={cn(isDone && !isCur && 'line-through decoration-border')}>{step.title}</span>
              </button>
            </li>
          )
        })}
      </ol>
    </aside>
  )
}
